import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Chip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Tabs,
  Tab,
  Stack
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SecurityIcon from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import DoorBackIcon from '@mui/icons-material/DoorBack';
import VideocamIcon from '@mui/icons-material/Videocam';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import moment from 'moment';

const SecurityAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const [responseError, setResponseError] = useState(false);
  
  // 模擬加載警報數據
  useEffect(() => {
    // 這裡將是API調用
    setTimeout(() => {
      const mockAlerts = [
        {
          id: 1,
          type: 'fire',
          location: 'A座18樓走廊',
          timestamp: '2023-06-20T15:30:00',
          description: '煙霧感應器被觸發，可能存在火災隱患',
          status: 'active',
          priority: 'high',
          camera: 'A-18-CORR-01',
          read: false
        },
        {
          id: 2,
          type: 'intrusion',
          location: 'B座后門',
          timestamp: '2023-06-20T14:45:00',
          description: '非授權進入，后門被強行打開',
          status: 'active',
          priority: 'high',
          camera: 'B-BACK-01',
          read: false
        },
        {
          id: 3,
          type: 'door',
          location: 'C座電梯大堂',
          timestamp: '2023-06-20T13:20:00',
          description: '逃生門被長時間打開',
          status: 'active',
          priority: 'medium',
          camera: 'C-LOBBY-02',
          read: true
        },
        {
          id: 4,
          type: 'camera',
          location: 'D座停車場',
          timestamp: '2023-06-20T10:15:00',
          description: '攝像頭離線，可能被人為干擾',
          status: 'resolved',
          priority: 'medium',
          response: '已派技術人員檢查，發現攝像頭電源線松動，已修復',
          resolvedBy: '張工程師',
          resolvedAt: '2023-06-20T11:30:00',
          camera: 'D-PARK-05',
          read: true
        },
        {
          id: 5,
          type: 'fire',
          location: 'E座廚房',
          timestamp: '2023-06-19T20:45:00',
          description: '煙霧濃度過高，可能有火災風險',
          status: 'resolved',
          priority: 'high',
          response: '經調查發現為住戶炒菜時煙霧過大，已提醒住戶注意廚房通風',
          resolvedBy: '李保安',
          resolvedAt: '2023-06-19T21:15:00',
          camera: 'E-KITCHEN-01',
          read: true
        }
      ];
      
      setAlerts(mockAlerts);
      setLoading(false);
    }, 1000);
  }, []);
  
  // 處理標籤切換
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // 打開警報詳情對話框
  const handleOpenDialog = (alert) => {
    setSelectedAlert(alert);
    setDialogOpen(true);
    
    // 如果警報未讀，則設為已讀
    if (!alert.read) {
      markAlertAsRead(alert.id);
    }
  };
  
  // 關閉警報詳情對話框
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // 打開響應對話框
  const handleOpenResponseDialog = () => {
    setDialogOpen(false);
    setResponseDialogOpen(true);
  };
  
  // 關閉響應對話框
  const handleCloseResponseDialog = () => {
    setResponseDialogOpen(false);
    setResponse('');
  };
  
  // 提交警報響應
  const handleSubmitResponse = () => {
    setResponseError(false);
    
    if (!response.trim()) {
      setResponseError(true);
      return;
    }
    
    try {
      // 這裡將是API調用
      const updatedAlerts = alerts.map(alert => {
        if (alert.id === selectedAlert.id) {
          return {
            ...alert,
            status: 'resolved',
            response: response,
            resolvedBy: '當前管理員',
            resolvedAt: moment().format()
          };
        }
        return alert;
      });
      
      setAlerts(updatedAlerts);
      setResponseDialogOpen(false);
      setResponseSubmitted(true);
      setResponse('');
      
      // 3秒後隱藏通知
      setTimeout(() => {
        setResponseSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('處理警報時發生錯誤:', error);
      setResponseError(true);
    }
  };
  
  // 將警報標記為已讀
  const markAlertAsRead = (alertId) => {
    const updatedAlerts = alerts.map(alert => {
      if (alert.id === alertId) {
        return { ...alert, read: true };
      }
      return alert;
    });
    
    setAlerts(updatedAlerts);
  };
  
  // 標記所有警報為已讀
  const markAllAsRead = () => {
    const updatedAlerts = alerts.map(alert => ({ ...alert, read: true }));
    setAlerts(updatedAlerts);
  };
  
  // 獲取警報圖標
  const getAlertIcon = (type, priority) => {
    let icon;
    let color;
    
    switch (type) {
      case 'fire':
        icon = <LocalFireDepartmentIcon />;
        color = 'error';
        break;
      case 'intrusion':
        icon = <SecurityIcon />;
        color = 'error';
        break;
      case 'door':
        icon = <DoorBackIcon />;
        color = 'warning';
        break;
      case 'camera':
        icon = <VideocamIcon />;
        color = 'warning';
        break;
      default:
        icon = <WarningIcon />;
        color = 'warning';
    }
    
    return <IconButton color={color} size="small">{icon}</IconButton>;
  };
  
  // 獲取優先級芯片
  const getPriorityChip = (priority) => {
    switch (priority) {
      case 'high':
        return <Chip size="small" label="高優先級" color="error" />;
      case 'medium':
        return <Chip size="small" label="中優先級" color="warning" />;
      case 'low':
        return <Chip size="small" label="低優先級" color="info" />;
      default:
        return <Chip size="small" label={priority} />;
    }
  };
  
  // 獲取警報類型名稱
  const getAlertTypeName = (type) => {
    switch (type) {
      case 'fire':
        return '火災警報';
      case 'intrusion':
        return '入侵警報';
      case 'door':
        return '門禁警報';
      case 'camera':
        return '攝像頭警報';
      default:
        return type;
    }
  };
  
  // 獲取警報狀態芯片
  const getStatusChip = (status) => {
    switch (status) {
      case 'active':
        return <Chip size="small" label="活躍" color="error" />;
      case 'pending':
        return <Chip size="small" label="待處理" color="warning" />;
      case 'resolved':
        return <Chip size="small" label="已解決" color="success" icon={<CheckCircleIcon />} />;
      default:
        return <Chip size="small" label={status} />;
    }
  };
  
  // 過濾警報
  const getFilteredAlerts = () => {
    switch (tabValue) {
      case 0: // 所有警報
        return alerts;
      case 1: // 活躍警報
        return alerts.filter(alert => alert.status === 'active');
      case 2: // 已解決
        return alerts.filter(alert => alert.status === 'resolved');
      case 3: // 高優先級
        return alerts.filter(alert => alert.priority === 'high');
      default:
        return alerts;
    }
  };
  
  const filteredAlerts = getFilteredAlerts();
  const unreadCount = alerts.filter(alert => !alert.read).length;
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">安全警報</Typography>
        
        {unreadCount > 0 && (
          <Button 
            variant="outlined" 
            startIcon={<CheckCircleIcon />}
            onClick={markAllAsRead}
          >
            標記所有為已讀
          </Button>
        )}
      </Box>
      
      {responseSubmitted && (
        <Alert severity="success" sx={{ mb: 2 }}>
          警報已成功處理！
        </Alert>
      )}
      
      {responseError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          處理警報時出現錯誤，請稍後再試！
        </Alert>
      )}
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>所有警報</span>
                <Chip 
                  size="small" 
                  label={alerts.length} 
                  sx={{ ml: 1, height: 20, minWidth: 20 }} 
                />
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>活躍警報</span>
                <Badge 
                  badgeContent={alerts.filter(a => a.status === 'active').length} 
                  color="error"
                  sx={{ ml: 1 }}
                />
              </Box>
            } 
          />
          <Tab label="已解決" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <span>高優先級</span>
                <Badge 
                  badgeContent={alerts.filter(a => a.priority === 'high').length} 
                  color="error"
                  sx={{ ml: 1 }}
                />
              </Box>
            } 
          />
        </Tabs>
      </Paper>
      
      {loading ? (
        <Typography align="center" sx={{ py: 4 }}>
          載入中...
        </Typography>
      ) : filteredAlerts.length === 0 ? (
        <Alert severity="info">
          沒有找到符合條件的警報
        </Alert>
      ) : (
        <List>
          {filteredAlerts.map(alert => (
            <Paper 
              key={alert.id} 
              elevation={1} 
              sx={{ 
                mb: 2, 
                borderLeft: `4px solid ${
                  alert.priority === 'high' ? '#f44336' : 
                  alert.priority === 'medium' ? '#ff9800' : 
                  '#2196f3'
                }`,
                bgcolor: alert.read ? 'background.paper' : 'rgba(33, 150, 243, 0.05)'
              }}
            >
              <ListItem 
                alignItems="flex-start"
                secondaryAction={
                  <IconButton edge="end" onClick={() => handleOpenDialog(alert)}>
                    <KeyboardArrowRightIcon />
                  </IconButton>
                }
                sx={{ cursor: 'pointer' }}
                onClick={() => handleOpenDialog(alert)}
              >
                <ListItemIcon>
                  {!alert.read ? (
                    <Badge color="primary" variant="dot">
                      {getAlertIcon(alert.type, alert.priority)}
                    </Badge>
                  ) : (
                    getAlertIcon(alert.type, alert.priority)
                  )}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" component="span">
                        {getAlertTypeName(alert.type)}
                      </Typography>
                      {getPriorityChip(alert.priority)}
                      {getStatusChip(alert.status)}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" component="span">
                          {alert.location}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" component="span">
                          {moment(alert.timestamp).format('YYYY/MM/DD HH:mm')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.primary">
                        {alert.description}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </Paper>
          ))}
        </List>
      )}
      
      {/* 警報詳情對話框 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {selectedAlert && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getAlertIcon(selectedAlert.type, selectedAlert.priority)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {getAlertTypeName(selectedAlert.type)}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      {getPriorityChip(selectedAlert.priority)}
                      {getStatusChip(selectedAlert.status)}
                    </Stack>
                    
                    <Typography variant="subtitle1" gutterBottom>
                      警報詳情
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body1" paragraph>
                        {selectedAlert.description}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            位置
                          </Typography>
                          <Typography variant="body1">
                            {selectedAlert.location}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            時間
                          </Typography>
                          <Typography variant="body1">
                            {moment(selectedAlert.timestamp).format('YYYY/MM/DD HH:mm')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            監控攝像頭
                          </Typography>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<VideocamIcon />}
                            sx={{ mt: 0.5 }}
                          >
                            查看 {selectedAlert.camera} 畫面
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                  
                  {selectedAlert.status === 'resolved' && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        解決方案
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body1" paragraph>
                          {selectedAlert.response}
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              處理人員
                            </Typography>
                            <Typography variant="body1">
                              {selectedAlert.resolvedBy}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              解決時間
                            </Typography>
                            <Typography variant="body1">
                              {moment(selectedAlert.resolvedAt).format('YYYY/MM/DD HH:mm')}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )}
                </Grid>
                
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" gutterBottom>
                    相關攝像頭畫面
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      height: 240, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'black',
                      color: 'white',
                      mb: 2
                    }}
                  >
                    <Typography>
                      攝像頭 {selectedAlert.camera} 畫面
                    </Typography>
                  </Paper>
                  
                  <Typography variant="subtitle1" gutterBottom>
                    位置地圖
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      height: 180, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#e0e0e0',
                      mb: 2
                    }}
                  >
                    <Typography>
                      {selectedAlert.location} 地圖
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedAlert.status !== 'resolved' && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleOpenResponseDialog}
                >
                  處理警報
                </Button>
              )}
              <Button onClick={handleCloseDialog}>關閉</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* 警報響應對話框 */}
      <Dialog open={responseDialogOpen} onClose={handleCloseResponseDialog} maxWidth="sm" fullWidth>
        {selectedAlert && (
          <>
            <DialogTitle>處理警報</DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" gutterBottom>
                {getAlertTypeName(selectedAlert.type)} - {selectedAlert.location}
              </Typography>
              
              <Alert severity={
                selectedAlert.priority === 'high' ? 'error' : 
                selectedAlert.priority === 'medium' ? 'warning' : 
                'info'
              } sx={{ mb: 3 }}>
                {selectedAlert.description}
              </Alert>
              
              <TextField
                fullWidth
                label="解決方案/響應措施"
                multiline
                rows={4}
                value={response}
                onChange={(e) => {
                  setResponse(e.target.value);
                  if (responseError && e.target.value.trim()) {
                    setResponseError(false);
                  }
                }}
                required
                error={responseError}
                helperText={responseError ? '請輸入有效的解決方案' : ''}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseResponseDialog}>取消</Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSubmitResponse}
                disabled={responseError}
              >
                提交並解決
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SecurityAlerts; 